/**
 * ContextMenuController - Context menu management for devices, canvas, library, and cables
 * 
 * Handles:
 * - Building different menus based on context (device/canvas/library/cable)
 * - Menu positioning within viewport bounds
 * - Action dispatch to app callbacks
 * - Outside click/touch dismissal
 */

// State
let contextMenuSpawnPosition = null; // Canvas position for "Add Device" from context menu

// Callbacks - set by initContextMenuController
let callbacks = {
    screenToCanvas: null,
    hasDeviceSelection: null,
    getSelectionDevices: null,
    setDeviceSelection: null,
    performCopy: null,
    performCut: null,
    performPaste: null,
    duplicateDevice: null,
    duplicateSelectedDevice: null,
    deleteSelected: null,
    deleteConnection: null,
    deleteSelectedCables: null,
    clearRouting: null,
    editInstanceLayout: null,
    editSelectedDevice: null,
    enterTouchMultiSelectMode: null,
    selectAllDevices: null,
    showAddDeviceModal: null,
    duplicateTemplate: null,
    deleteTemplate: null,
    getSelectedTemplateId: null,
    setSelectedTemplateId: null,
    getClipboardState: null
};

/**
 * Initialize the context menu controller with app callbacks
 * @param {Object} config - Configuration with callbacks
 */
export function initContextMenuController(config) {
    callbacks = { ...callbacks, ...config };
}

/**
 * Show context menu at specified position
 * @param {number} x - Screen X coordinate
 * @param {number} y - Screen Y coordinate
 * @param {string} menuType - 'device' | 'canvas' | 'library' | 'cable'
 * @param {Object} context - Context data (deviceId, templateId, connectionId, clickX/Y)
 */
export function showContextMenu(x, y, menuType, context) {
    const menu = document.getElementById('context-menu');
    let html = '';
    
    // Store canvas position for "Add Device" action
    if (menuType === 'canvas' && context.clickX !== undefined) {
        contextMenuSpawnPosition = callbacks.screenToCanvas?.(context.clickX, context.clickY) || null;
    } else {
        contextMenuSpawnPosition = null;
    }
    
    if (menuType === 'device') {
        html = buildDeviceMenu();
        menu.dataset.deviceId = context.deviceId;
    } else if (menuType === 'canvas') {
        html = buildCanvasMenu();
    } else if (menuType === 'library') {
        html = buildLibraryMenu();
        menu.dataset.templateId = context.templateId;
    } else if (menuType === 'cable') {
        html = buildCableMenu();
        menu.dataset.connectionId = context.connectionId;
    }
    
    menu.innerHTML = html;
    
    // Position menu (keep within viewport)
    const menuWidth = 180;
    const menuHeight = menu.childElementCount * 48;
    const maxX = window.innerWidth - menuWidth - 10;
    const maxY = window.innerHeight - menuHeight - 10;
    
    menu.style.left = Math.min(x, maxX) + 'px';
    menu.style.top = Math.min(y, maxY) + 'px';
    menu.classList.add('visible');
    
    // Close on tap outside (with delay to prevent immediate close)
    setTimeout(() => {
        document.addEventListener('touchstart', closeContextMenuOnOutsideTouch, { once: true });
        document.addEventListener('mousedown', closeContextMenuOnOutsideClick, { once: true });
    }, 10);
}

/**
 * Close the context menu
 */
export function closeContextMenu() {
    const menu = document.getElementById('context-menu');
    menu.classList.remove('visible');
    document.removeEventListener('touchstart', closeContextMenuOnOutsideTouch);
    document.removeEventListener('mousedown', closeContextMenuOnOutsideClick);
}

/**
 * Handle context menu action
 * @param {string} action - Action name
 */
export function contextMenuAction(action) {
    const menu = document.getElementById('context-menu');
    const deviceId = menu.dataset.deviceId;
    const templateId = menu.dataset.templateId;
    
    closeContextMenu();
    
    switch (action) {
        case 'copy':
            handleCopyAction(deviceId);
            break;
        case 'cut':
            handleCutAction(deviceId);
            break;
        case 'duplicate':
            handleDuplicateAction(deviceId);
            break;
        case 'editLayout':
            if (deviceId) {
                callbacks.setDeviceSelection?.(deviceId);
                callbacks.editInstanceLayout?.();
            }
            break;
        case 'editConfig':
            if (deviceId) {
                callbacks.setDeviceSelection?.(deviceId);
                callbacks.editSelectedDevice?.();
            }
            break;
        case 'delete':
            handleDeleteAction(deviceId);
            break;
        case 'multiSelect':
            if (deviceId) {
                callbacks.enterTouchMultiSelectMode?.(deviceId);
            }
            break;
        case 'paste':
            callbacks.performPaste?.();
            break;
        case 'selectAll':
            callbacks.selectAllDevices?.();
            break;
        case 'addDevice':
            callbacks.showAddDeviceModal?.();
            break;
        case 'editDefinition':
            if (templateId) {
                callbacks.setSelectedTemplateId?.(templateId);
                callbacks.editSelectedDevice?.();
            }
            break;
        case 'duplicateTemplate':
            if (templateId) {
                callbacks.duplicateTemplate?.(templateId);
            }
            break;
        case 'deleteTemplate':
            if (templateId) {
                callbacks.deleteTemplate?.(templateId);
            }
            break;
        case 'deleteCable':
            const connectionId = menu.dataset.connectionId;
            if (connectionId) {
                callbacks.deleteConnection?.(connectionId);
            }
            break;
        case 'clearRouting':
            // Ensure right-clicked device is in selection, then clear routing
            if (deviceId) {
                const selectionDevices = callbacks.getSelectionDevices?.();
                if (!selectionDevices?.has(deviceId)) {
                    callbacks.setDeviceSelection?.(deviceId);
                }
            }
            callbacks.clearRouting?.();
            break;
    }
}

/**
 * Get the stored spawn position for "Add Device" action
 * @returns {Object|null} Canvas position {x, y} or null
 */
export function getContextMenuSpawnPosition() {
    return contextMenuSpawnPosition;
}

/**
 * Clear the spawn position (after use or on cancel)
 */
export function clearContextMenuSpawnPosition() {
    contextMenuSpawnPosition = null;
}

// ============= PRIVATE HELPERS =============

function closeContextMenuOnOutsideTouch(e) {
    const menu = document.getElementById('context-menu');
    if (!menu.contains(e.target)) {
        closeContextMenu();
    }
}

function closeContextMenuOnOutsideClick(e) {
    const menu = document.getElementById('context-menu');
    if (!menu.contains(e.target)) {
        closeContextMenu();
    }
}

function buildDeviceMenu() {
    return `
        <button onclick="contextMenuAction('copy')"><span class="icon">📋</span> Copy</button>
        <button onclick="contextMenuAction('cut')"><span class="icon">✂️</span> Cut</button>
        <button onclick="contextMenuAction('duplicate')"><span class="icon">📄</span> Duplicate</button>
        <div class="divider"></div>
        <button onclick="contextMenuAction('editLayout')"><span class="icon">📐</span> Edit Layout</button>
        <button onclick="contextMenuAction('editConfig')"><span class="icon">⚙️</span> Edit Config</button>
        <button onclick="contextMenuAction('multiSelect')"><span class="icon">☑️</span> Multi-select</button>
        <div class="divider"></div>
        <button onclick="contextMenuAction('clearRouting')"><span class="icon">🔌</span> Clear Routing</button>
        <button class="danger" onclick="contextMenuAction('delete')"><span class="icon">🗑️</span> Delete</button>
    `;
}

function buildCanvasMenu() {
    // If there's an active selection, show selection operations
    if (callbacks.hasDeviceSelection?.()) {
        return `
            <button onclick="contextMenuAction('copy')"><span class="icon">📋</span> Copy</button>
            <button onclick="contextMenuAction('cut')"><span class="icon">✂️</span> Cut</button>
            <button onclick="contextMenuAction('duplicate')"><span class="icon">📄</span> Duplicate</button>
            <div class="divider"></div>
            <button class="danger" onclick="contextMenuAction('delete')"><span class="icon">🗑️</span> Delete</button>
        `;
    }
    
    // Check if clipboard has content
    const clipboardState = callbacks.getClipboardState?.() || { hasContent: false };
    
    let html = '';
    if (clipboardState.hasContent) {
        html += `<button onclick="contextMenuAction('paste')"><span class="icon">📋</span> Paste</button>`;
    }
    html += `
        <button onclick="contextMenuAction('selectAll')"><span class="icon">☑️</span> Select All</button>
        <div class="divider"></div>
        <button onclick="contextMenuAction('addDevice')"><span class="icon">➕</span> Add Device</button>
    `;
    return html;
}

function buildLibraryMenu() {
    return `
        <button onclick="contextMenuAction('editDefinition')"><span class="icon">✏️</span> Edit Definition</button>
        <button onclick="contextMenuAction('duplicateTemplate')"><span class="icon">📄</span> Duplicate</button>
        <div class="divider"></div>
        <button class="danger" onclick="contextMenuAction('deleteTemplate')"><span class="icon">🗑️</span> Delete</button>
    `;
}

function buildCableMenu() {
    return `
        <button class="danger" onclick="contextMenuAction('deleteCable')"><span class="icon">🗑️</span> Delete</button>
    `;
}

function handleCopyAction(deviceId) {
    if (deviceId) {
        // If device not in selection, select just this device
        const selectionDevices = callbacks.getSelectionDevices?.();
        if (!selectionDevices?.has(deviceId)) {
            callbacks.setDeviceSelection?.(deviceId);
        }
        callbacks.performCopy?.();
    } else if (callbacks.hasDeviceSelection?.()) {
        // Canvas context menu with active selection
        callbacks.performCopy?.();
    }
}

function handleCutAction(deviceId) {
    if (deviceId) {
        const selectionDevices = callbacks.getSelectionDevices?.();
        if (!selectionDevices?.has(deviceId)) {
            callbacks.setDeviceSelection?.(deviceId);
        }
        callbacks.performCut?.();
    } else if (callbacks.hasDeviceSelection?.()) {
        callbacks.performCut?.();
    }
}

function handleDuplicateAction(deviceId) {
    if (deviceId) {
        // If device is part of multi-selection, duplicate all selected
        const selectionDevices = callbacks.getSelectionDevices?.();
        if (selectionDevices?.has(deviceId) && selectionDevices.size > 1) {
            callbacks.duplicateSelectedDevice?.();
        } else {
            callbacks.duplicateDevice?.(deviceId, 30, 30);
        }
    } else if (callbacks.hasDeviceSelection?.()) {
        callbacks.duplicateSelectedDevice?.();
    }
}

function handleDeleteAction(deviceId) {
    if (deviceId) {
        const selectionDevices = callbacks.getSelectionDevices?.();
        if (!selectionDevices?.has(deviceId)) {
            callbacks.setDeviceSelection?.(deviceId);
        }
        callbacks.deleteSelected?.();
    } else if (callbacks.hasDeviceSelection?.()) {
        callbacks.deleteSelected?.();
    }
}
